import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { ToastService } from '@core/toast/toast.service';
import { showServerNotRespondWarningWhenDegraded, showToast } from '@core/errors/models/context';
import { CurrentLoginFlowService } from '@core/sso/services/current-login-flow.service';
import { LoginStatusFacade } from '@core/login-status/facades/login-status.facade';

export function errorsInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const toastService = inject(ToastService);
  const currentLoginFlowService = inject(CurrentLoginFlowService);
  const loginStatusFacade = inject(LoginStatusFacade);

  return next(request).pipe(
    catchError((errorResponse: HttpErrorResponse) => {
      const { status } = errorResponse;

      if (
        errorResponse.error instanceof ProgressEvent &&
        navigator.onLine &&
        !currentLoginFlowService.isOpenIdConnectFlow() &&
        shouldShowServerNotRespondWarning(
          request.context.get(showServerNotRespondWarningWhenDegraded),
          loginStatusFacade.isLoginDegradationBannerEnabled()
        )
      ) {
        toastService.showWarningToast('general-errors.server-not-respond');
      }

      if (isServerError(status)) {
        handleServerError(errorResponse, toastService, request.context.get(showToast));
      }

      return throwError(() => errorResponse);
    })
  );
}

function shouldShowServerNotRespondWarning(isRequestAllowedToWarn: boolean, isLoginDegradationBannerEnabled: boolean): boolean {
  return !isLoginDegradationBannerEnabled || isRequestAllowedToWarn;
}

function isServerError(status: HttpStatusCode): boolean {
  return status >= HttpStatusCode.InternalServerError && status <= HttpStatusCode.NetworkAuthenticationRequired;
}

function handleServerError({ error, status }: HttpErrorResponse, toastService: ToastService, shouldShowToast: boolean): void {
  const errorKey = getServerErrorKey(status);

  if (shouldShowToast) {
    toastService.showErrorToast(errorKey);
  }

  console.error(`Error correlation ID: ${error}`);
}

function getServerErrorKey(status: HttpStatusCode): string {
  switch (status) {
    case HttpStatusCode.NotImplemented:
      return 'bis-migration.feature-unavailable';
    default:
      return 'general-errors.internal-server-error';
  }
}
